import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Tab,
  Tabs,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Configurar plugins de dayjs
dayjs.extend(utc)
dayjs.extend(timezone)

interface CronSelectorProps {
  handleChange: (cron: string, startDate: Date, finishDate: Date) => void
}

type RecurrenceType = 'daily' | 'weekly' | 'monthly'

const WEEKDAYS = [
  { key: 'monday', value: 1 },
  { key: 'tuesday', value: 2 },
  { key: 'wednesday', value: 3 },
  { key: 'thursday', value: 4 },
  { key: 'friday', value: 5 },
  { key: 'saturday', value: 6 },
  { key: 'sunday', value: 0 },
]

export const CronItemMockup: React.FC<CronSelectorProps> = ({ handleChange }) => {
  const { t } = useTranslation()

  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>('daily')
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([])
  const [monthDay, setMonthDay] = useState<number>(1)
  const [startDate, setStartDate] = useState<Dayjs>(dayjs())
  const [finishDate, setFinishDate] = useState<Dayjs>(dayjs().add(1, 'month'))

  const generateCron = (): string => {
    const utcHour = startDate.utc().hour()
    const utcMinute = startDate.utc().minute()

    switch (recurrenceType) {
      case 'daily':
        return `${utcMinute} ${utcHour} * * *`

      case 'weekly':
        const days = selectedWeekdays.length > 0 ? selectedWeekdays.join(',') : '*'
        return `${utcMinute} ${utcHour} * * ${days}`

      case 'monthly':
        return `${utcMinute} ${utcHour} ${monthDay} * *`

      default:
        return `${utcMinute} ${utcHour} * * *`
    }
  }

  useEffect(() => {
    const cron = generateCron()
    // Convertir a Date manteniendo la hora local
    const startDateLocal = new Date(
      startDate.year(),
      startDate.month(),
      startDate.date(),
      startDate.hour(),
      startDate.minute(),
      startDate.second()
    )

    const finishDateLocal = new Date(
      finishDate.year(),
      finishDate.month(),
      finishDate.date(),
      finishDate.hour(),
      finishDate.minute(),
      finishDate.second()
    )

    handleChange(cron, startDateLocal, finishDateLocal)
  }, [recurrenceType, selectedWeekdays, monthDay, startDate, finishDate])

  const handleRecurrenceTypeChange = (_: React.SyntheticEvent, newValue: number) => {
    const types: RecurrenceType[] = ['daily', 'weekly', 'monthly']
    setRecurrenceType(types[newValue])
  }

  const handleWeekdayChange = (event: any) => {
    const value = event.target.value as number[]
    setSelectedWeekdays(value)
  }

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setStartDate(newValue)
      if (finishDate.isBefore(newValue)) {
        setFinishDate(newValue.add(1, 'month'))
      }
    }
  }

  const handleFinishDateChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isAfter(startDate)) {
      setFinishDate(newValue)
    }
  }

  const getTabIndex = (): number => {
    const types: RecurrenceType[] = ['daily', 'weekly', 'monthly']
    return types.indexOf(recurrenceType)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ minWidth: 150 }}>
          <Tabs
            orientation="vertical"
            value={getTabIndex()}
            onChange={handleRecurrenceTypeChange}
            sx={{ borderRight: 1, borderColor: 'divider' }}
          >
            <Tab label={t('daily')} />
            <Tab label={t('weekly')} />
            <Tab label={t('monthly')} />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, minWidth: 300 }}>
          {recurrenceType === 'daily' && (
            <Box>
              <p>{t('eventDaily')}</p>
            </Box>
          )}

          {recurrenceType === 'weekly' && (
            <Box>
              <p style={{ marginBottom: 16 }}>{t('selectWeekdays')}:</p>
              <Select
                multiple
                value={selectedWeekdays}
                onChange={handleWeekdayChange}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((value) => {
                      const weekday = WEEKDAYS.find((w) => w.value === value)
                      return (
                        <Chip key={value} label={weekday ? t(weekday.key) : value} size="small" />
                      )
                    })}
                  </Box>
                )}
                sx={{ width: '100%', maxWidth: 400 }}
              >
                {WEEKDAYS.map((weekday) => (
                  <MenuItem key={weekday.key} value={weekday.value}>
                    <Checkbox checked={selectedWeekdays.includes(weekday.value)} />
                    <ListItemText primary={t(weekday.key)} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {recurrenceType === 'monthly' && (
            <Box>
              <p style={{ marginBottom: 16 }}>{t('dayOfMonth')}:</p>
              <TextField
                type="number"
                value={monthDay}
                onChange={(e) =>
                  setMonthDay(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))
                }
                inputProps={{ min: 1, max: 31 }}
                sx={{ width: 100 }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ minWidth: 300 }}>
          <h4>{t('rangeOfRepetition')}</h4>

          <Box sx={{ mb: 2 }}>
            <p style={{ fontSize: 12, marginBottom: 8 }}>{t('start')}:</p>
            <DateTimePicker<Dayjs>
              value={startDate}
              onChange={handleStartDateChange}
              minDateTime={dayjs()}
              renderInput={(props) => (
                <TextField
                  {...props}
                  size="small"
                  sx={{ width: '100%' }}
                  inputProps={{
                    ...props.inputProps,
                    readOnly: true,
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <p style={{ fontSize: 12, marginBottom: 8 }}>{t('finishAt')}:</p>
            <DateTimePicker<Dayjs>
              value={finishDate}
              onChange={handleFinishDateChange}
              minDateTime={startDate}
              renderInput={(props) => (
                <TextField
                  {...props}
                  size="small"
                  sx={{ width: '100%' }}
                  inputProps={{
                    ...props.inputProps,
                    readOnly: true,
                  }}
                />
              )}
            />
          </Box>

          <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
            <small>
              Cron: {recurrenceType} {generateCron()}
            </small>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  )
}
